/* Created by Interactive 63 - https://interactive63.com */
/**
 * Easing for the runtime. Only three forms reach it: linear, cubic Bézier and sampled points.
 * Keywords become cubics, and steps, holds, springs, bounces and elastics become points before
 * export, so the runtime carries no physics or stepping code. This file imports only the small
 * cubic solver, to keep the runtime bundle small.
 */
import { cubicBezier, type EasingFunction } from './cubic-bezier';

export type Point = [x: number, y: number];

export type RuntimeEasing =
  | { type: 'linear' }
  | { type: 'cubic'; x1: number; y1: number; x2: number; y2: number }
  | { type: 'points'; points: Point[] };

/** Piecewise-linear interpolation over a point list, clamped at both ends. */
function evaluatePoints(points: readonly Point[], t: number): number {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return t;
  if (t <= first[0]) return first[1];
  if (t >= last[0]) return last[1];
  for (let i = 1; i < points.length; i += 1) {
    const to = points[i];
    const from = points[i - 1];
    if (!to || !from || t >= to[0]) continue;
    const span = to[0] - from[0];
    return span === 0 ? to[1] : from[1] + ((to[1] - from[1]) * (t - from[0])) / span;
  }
  return last[1];
}

/** Builds the function for an easing. Build it once and call it for every frame. */
export function runtimeEasing(easing: RuntimeEasing): EasingFunction {
  if (easing.type === 'cubic') return cubicBezier(easing.x1, easing.y1, easing.x2, easing.y2);
  if (easing.type === 'points') return (t) => evaluatePoints(easing.points, t);
  return (t) => t;
}

/** Returns the easing at progress t. The result may overshoot 0..1. */
export function evaluateRuntimeEasing(easing: RuntimeEasing, t: number): number {
  return runtimeEasing(easing)(t);
}
